import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, Wifi, QrCode, Pencil, ScrollText } from "lucide-react";
import { statusMeta, resultMeta, fmtTime, fmtDateTime } from "@/pages/hr/attendanceUtils";

const STATUSES = ["", "clocked_in", "completed", "unscheduled", "exception_pending", "exception_approved", "exception_rejected"];

export default function RecordsPanel() {
  const { can } = useAuth();
  const canManage = can("attendance:manage");
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [f, setF] = useState({ date: "", employee_id: "all", department_id: "all", status: "all" });
  const [correct, setCorrect] = useState(null);
  const [cf, setCf] = useState({ clock_in_ts: "", clock_out_ts: "", status: "", reason: "" });
  const [audit, setAudit] = useState(null);

  const load = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.append(k, v); });
    api.get(`/hr/attendance?${p.toString()}`).then(r => setRows(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => {
    api.get("/hr/employees").then(r => setEmployees(r.data)).catch(() => {});
    api.get("/hr/departments").then(r => setDepartments(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  const openCorrect = (r) => {
    setCorrect(r);
    setCf({ clock_in_ts: fmtTime(r.clock_in?.server_ts) === "—" ? "" : fmtTime(r.clock_in?.server_ts),
            clock_out_ts: fmtTime(r.clock_out?.server_ts) === "—" ? "" : fmtTime(r.clock_out?.server_ts),
            status: r.status, reason: "" });
  };
  const saveCorrect = async () => {
    if (!cf.reason.trim()) return toast.error("A reason is required");
    try {
      const body = { reason: cf.reason, status: cf.status || null };
      if (cf.clock_in_ts) body.clock_in_ts = cf.clock_in_ts;
      if (cf.clock_out_ts) body.clock_out_ts = cf.clock_out_ts;
      await api.post(`/hr/attendance/${correct.id}/correct`, body);
      toast.success("Corrected"); setCorrect(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openAudit = async (r) => {
    try { const { data } = await api.get(`/hr/attendance/${r.id}/audit`); setAudit({ rec: r, entries: data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ResultCell = ({ punch }) => punch ? (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-xs">{fmtTime(punch.server_ts)}{punch.corrected && <span className="text-amber-500" title="Corrected"> *</span>}</span>
      <span className="flex gap-1">
        {punch.location_result !== "not_required" && <Badge variant="outline" className={`${resultMeta(punch.location_result).cls} text-[10px] px-1 py-0`} title={`${punch.location_distance_m ?? "?"}m`}><MapPin className="w-2.5 h-2.5" /></Badge>}
        {punch.network_result !== "not_required" && <Badge variant="outline" className={`${resultMeta(punch.network_result).cls} text-[10px] px-1 py-0`} title={punch.ip || ""}><Wifi className="w-2.5 h-2.5" /></Badge>}
        {punch.qr_result && punch.qr_result !== "not_required" && <Badge variant="outline" className={`${resultMeta(punch.qr_result).cls} text-[10px] px-1 py-0`} title="Office QR"><QrCode className="w-2.5 h-2.5" /></Badge>}
      </span>
    </div>
  ) : <span className="text-slate-300">—</span>;

  return (
    <div data-testid="att-records-panel">
      <div className="flex flex-wrap items-end gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
        <div><Label className="text-xs">Date</Label><Input type="date" data-testid="att-filter-date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} className="h-9 w-40" /></div>
        <div><Label className="text-xs">Employee</Label>
          <Select value={f.employee_id} onValueChange={v => setF({ ...f, employee_id: v })}>
            <SelectTrigger data-testid="att-filter-employee" className="h-9 w-48"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Department</Label>
          <Select value={f.department_id} onValueChange={v => setF({ ...f, department_id: v })}>
            <SelectTrigger data-testid="att-filter-department" className="h-9 w-48"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="att-filter-status" className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{STATUSES.filter(Boolean).map(s => <SelectItem key={s} value={s}>{statusMeta(s).label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button variant="outline" className="h-9" data-testid="att-filter-clear" onClick={() => setF({ date: "", employee_id: "all", department_id: "all", status: "all" })}>Clear</Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="att-records-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Employee</th><th className="text-left px-3 py-2">Scheduled</th><th className="text-left px-3 py-2">In</th><th className="text-left px-3 py-2">Out</th><th className="text-left px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">No records</td></tr>}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`att-row-${r.id}`}>
                <td className="px-3 py-2 font-mono">{r.date}</td>
                <td className="px-3 py-2"><div className="font-medium">{r.full_name}</div><div className="text-xs text-slate-400 font-mono">{r.employee_id}</div></td>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.schedule?.is_scheduled ? `${r.schedule.scheduled_start || "—"}–${r.schedule.scheduled_end || "—"}` : "—"}</td>
                <td className="px-3 py-2"><ResultCell punch={r.clock_in} /></td>
                <td className="px-3 py-2"><ResultCell punch={r.clock_out} /></td>
                <td className="px-3 py-2"><Badge variant="outline" className={statusMeta(r.status).cls}>{statusMeta(r.status).label}</Badge></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => openAudit(r)} data-testid={`att-audit-${r.id}`} title="Audit" className="text-slate-400 hover:text-slate-600 mr-2"><ScrollText className="w-4 h-4 inline" /></button>
                  {canManage && <button onClick={() => openCorrect(r)} data-testid={`att-correct-${r.id}`} title="Correct" className="text-sky-600 hover:text-sky-800"><Pencil className="w-4 h-4 inline" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!correct} onOpenChange={v => !v && setCorrect(null)}>
        <DialogContent data-testid="att-correct-dialog">
          <DialogHeader><DialogTitle>Correct Attendance — {correct?.full_name} ({correct?.date})</DialogTitle>
            <DialogDescription>Times are Malaysia time (MYT). A reason is required and every change is audited.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Clock In (HH:MM, MYT)</Label><Input data-testid="att-correct-in" value={cf.clock_in_ts} onChange={e => setCf({ ...cf, clock_in_ts: e.target.value })} placeholder="09:00" /></div>
            <div><Label>Clock Out (HH:MM, MYT)</Label><Input data-testid="att-correct-out" value={cf.clock_out_ts} onChange={e => setCf({ ...cf, clock_out_ts: e.target.value })} placeholder="17:00" /></div>
          </div>
          <div><Label>Status</Label>
            <Select value={cf.status} onValueChange={v => setCf({ ...cf, status: v })}>
              <SelectTrigger data-testid="att-correct-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.filter(Boolean).map(s => <SelectItem key={s} value={s}>{statusMeta(s).label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Reason *</Label><Textarea data-testid="att-correct-reason" value={cf.reason} onChange={e => setCf({ ...cf, reason: e.target.value })} rows={2} placeholder="Why is this correction being made?" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setCorrect(null)}>Cancel</Button><Button onClick={saveCorrect} data-testid="att-correct-save" className="bg-slate-900">Save Correction</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!audit} onOpenChange={v => !v && setAudit(null)}>
        <DialogContent data-testid="att-audit-dialog">
          <DialogHeader><DialogTitle>Audit History — {audit?.rec?.full_name} ({audit?.rec?.date})</DialogTitle>
            <DialogDescription>Every admin correction with the before-state, reason and who made it.</DialogDescription></DialogHeader>
          {audit?.entries?.length === 0 ? <p className="text-sm text-slate-400">No corrections recorded.</p> : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {audit?.entries?.map(a => (
                <div key={a.id} className="rounded border border-slate-200 p-2 text-xs">
                  <div className="flex justify-between"><span className="font-medium">{a.changed_by}</span><span className="text-slate-400">{fmtDateTime(a.changed_at)}</span></div>
                  <div className="text-slate-600 mt-1">{a.reason}</div>
                  <div className="text-slate-400 mt-1 font-mono">before: {JSON.stringify(a.before)}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
